import React, { useState, useEffect, useRef } from 'react';

/* ──────────────────────────────────────────────────────────────────
 * BillingG703 — Region 2 G703 continuation sheet
 *
 * AIA G703 line table. 12 columns matching the design spec:
 *   Item · Description · Sched · Previous · This period · % comp ·
 *   Stored · Total · Balance · Retainage · Released · (kebab)
 *
 * Features:
 *  - Sticky table header (top: 0, height: 38)
 *  - Sticky section subheaders (top: 38) — grouped by sov_section /
 *    building_area_phase; each subheader has a caret, dot (grey →
 *    success at 100%), name, line count, and right-aligned subtotals.
 *  - Inline-edit cells on Draft pay apps. Click cell → input with
 *    accent border + 3px ring + 60% accent-soft tint. Pending dot
 *    top-right while save in flight; flips to red error dot on
 *    failure (tooltip carries the message).
 *  - Over-billed inset stripe (3px box-shadow inset, --danger) when
 *    Total Completed + Stored exceeds Scheduled.
 *  - Sticky totals footer (bottom: 0, height 40) — same grid columns
 *    as the table; mono tabular sums; aggregate % complete bar.
 *  - Locked / draft hint in the card header.
 * ────────────────────────────────────────────────────────────────── */

/* ─────────────────────── Helpers ─────────────────────── */

const $f0 = (v) => {
  const n = parseFloat(v) || 0;
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const $f2 = (v) => {
  const n = parseFloat(v) || 0;
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const num = (v) => parseFloat(v) || 0;

/* ─────────────────────── Inline-SVG icons ─────────────────────── */

const ChevronRight = ({ size = 12, expanded }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{
    transform: expanded ? 'rotate(90deg)' : 'none',
    transition: 'transform .15s',
  }}>
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const LockIcon = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const KebabIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" />
  </svg>
);

/* ───────────────── Schedule-progress alerts ─────────────────
 * SOV lines whose linked tasks lag their expected (date-prorated)
 * progress are flagged so a biller can see, at a glance, what's behind
 * before finalizing. Driven by GET /billing/:id/progress-alerts.
 *   critical = 20%+ behind  → rose stripe + tint
 *   warning  = 10%+ behind  → yellow (amber) stripe + tint
 *   info     = <10% behind  → listed in the banner, no row tint
 * (Restored after the structural redesign dropped the prior banner +
 * per-row highlighting; the backend endpoint was always intact.)
 */
const SEV = {
  critical: { line: 'var(--danger)',  text: 'var(--danger)',  soft: 'var(--danger-soft)' },
  warning:  { line: 'var(--warning)', text: 'var(--warning)', soft: 'var(--warning-soft)' },
  info:     { line: 'var(--accent)',  text: 'var(--text-2)',  soft: 'var(--bg-surface-2)' },
};
const rowTint = (sev) => sev === 'critical'
  ? 'color-mix(in oklch, var(--danger) 7%, var(--bg-surface))'
  : 'color-mix(in oklch, var(--warning) 10%, var(--bg-surface))';
const rowTintHover = (sev) => sev === 'critical'
  ? 'color-mix(in oklch, var(--danger) 13%, var(--bg-surface))'
  : 'color-mix(in oklch, var(--warning) 17%, var(--bg-surface))';

const ProgressAlertBanner = ({ alerts }) => {
  const [open, setOpen] = useState(false);
  if (!alerts || !alerts.total_alerts) return null;
  const hasCrit = alerts.critical_count > 0;
  const tone = hasCrit ? 'critical' : alerts.warning_count > 0 ? 'warning' : 'info';
  const c = SEV[tone];
  return (
    <div style={{
      margin: '12px 16px 0',
      border: `1px solid color-mix(in oklch, ${c.line} 30%, transparent)`,
      background: c.soft,
      borderRadius: 10,
      overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px' }}>
        <span style={{ fontSize: 17.5, lineHeight: 1, marginTop: 1, flexShrink: 0 }}>{hasCrit ? '⚠️' : '📋'}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: c.text }}>
            {alerts.total_alerts} task{alerts.total_alerts !== 1 ? 's' : ''} behind schedule
            {alerts.days_until_end <= 7 && (
              <span style={{ fontWeight: 500, color: 'var(--text-3)', marginLeft: 6 }}>
                {'— '}
                {alerts.days_until_end <= 0
                  ? 'period has ended'
                  : `${alerts.days_until_end} day${alerts.days_until_end !== 1 ? 's' : ''} until period end`}
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 2, lineHeight: 1.5 }}>
            {alerts.critical_count > 0 && `${alerts.critical_count} critical (20%+ behind). `}
            {alerts.warning_count > 0 && `${alerts.warning_count} warning (10%+ behind). `}
            Update task % complete or task dates to reflect actual progress.
          </div>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            style={{
              marginTop: 6, background: 'none', border: 'none', padding: 0,
              fontSize: 13, fontWeight: 600, color: c.text, cursor: 'pointer',
            }}
          >
            {open ? 'Hide details' : 'View details'}
          </button>
          {open && (
            <div style={{ marginTop: 8, maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {alerts.alerts.map((a, i) => {
                const ac = SEV[a.severity] || SEV.info;
                return (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                    padding: '6px 10px', borderRadius: 6,
                    background: 'var(--bg-surface)',
                    border: `1px solid color-mix(in oklch, ${ac.line} 25%, transparent)`,
                    borderLeft: `3px solid ${ac.line}`,
                  }}>
                    <div style={{ minWidth: 0, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: 600, color: 'var(--text-1)' }}>{a.task_code || a.task_name}</span>
                      {a.sov_code && <span className="mono" style={{ color: 'var(--text-3)', marginLeft: 6 }}>{a.sov_code}</span>}
                      <span style={{ color: 'var(--text-3)', marginLeft: 6 }}>{'— '}{a.message}</span>
                    </div>
                    <span className="mono" style={{
                      flexShrink: 0, fontSize: 12, fontWeight: 600,
                      padding: '2px 8px', borderRadius: 999,
                      color: '#fff', background: ac.line,
                    }}>
                      {Math.round(a.actual_percent)}% / {Math.round(a.expected_percent)}%
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────── Editable cell ─────────────────────── */

const EditCell = ({
  value, onCommit, suffix = '', kind = 'currency', // 'currency' | 'percent'
  pending, errorMsg, disabled, align = 'right',
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const inputRef = useRef(null);

  useEffect(() => { if (!editing) setDraft(value ?? ''); }, [value, editing]);
  useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);

  const displayN = num(value);
  const muted = displayN === 0;
  const formatted = kind === 'percent'
    ? `${displayN.toFixed(1)}%`
    : `$${displayN.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const cellBase = {
    position: 'relative', height: 38, padding: '0 10px',
    display: 'flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
    fontFamily: 'var(--font-mono)', fontSize: 13,
    fontWeight: kind === 'percent' ? 400 : 500,
    color: muted && !pending ? 'var(--text-4)' : 'var(--text-1)',
    letterSpacing: '-0.02em',
    cursor: disabled ? 'default' : 'text',
    overflow: 'hidden',
  };

  if (!editing) {
    return (
      <div
        onClick={() => !disabled && setEditing(true)}
        style={{
          ...cellBase,
          background: 'transparent',
        }}
        onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = 'color-mix(in oklch, var(--accent) 4%, transparent)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        title={errorMsg || ''}
      >
        {formatted}
        {(pending || errorMsg) && (
          <span style={{
            position: 'absolute', top: 6, right: 6,
            width: 5, height: 5, borderRadius: 99,
            background: errorMsg ? 'var(--danger)' : 'var(--accent)',
            boxShadow: `0 0 0 2px color-mix(in oklch, ${errorMsg ? 'var(--danger)' : 'var(--accent)'} 18%, transparent)`,
          }} />
        )}
      </div>
    );
  }

  return (
    <div style={{
      ...cellBase, padding: '4px 6px',
    }}>
      <input
        ref={inputRef}
        type="number" step={kind === 'percent' ? '0.1' : '0.01'}
        defaultValue={value ?? ''}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
          else if (e.key === 'Escape') { setEditing(false); setDraft(value ?? ''); }
        }}
        onBlur={() => {
          const next = draft === '' ? 0 : parseFloat(draft);
          if (!isNaN(next) && Math.abs(next - num(value)) > 0.001) {
            onCommit(next);
          }
          setEditing(false);
        }}
        style={{
          width: '100%', height: 30, padding: '0 8px',
          background: 'color-mix(in oklch, var(--accent-soft) 60%, var(--bg-surface))',
          border: 'none',
          borderRadius: 6,
          fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 'inherit',
          letterSpacing: 'inherit',
          color: 'var(--text-1)',
          textAlign: align,
          outline: 'none',
          boxShadow: '0 0 0 1.5px var(--accent), 0 0 0 4.5px var(--accent-ring)',
        }}
      />
      {suffix && (
        <span style={{ color: 'var(--text-3)', fontSize: 12.5, marginLeft: 4 }}>{suffix}</span>
      )}
    </div>
  );
};

/* ─────────────────────── Section grouping ─────────────────────── */

// Server returns sov_section_name (from a LEFT JOIN on the sections
// table in GET /api/billing-periods/:id). Older callers used different
// names so we accept several for backwards compatibility before falling
// back to 'Other' for SOV lines that genuinely aren't sectioned.
// 'Other' should be rare in practice -- only lines whose sov_lines row
// has section_id IS NULL land here.
const sectionKey = (line) =>
  line.sov_section_name ||
  line.sov_section ||
  line.section_name ||
  line.building_area_phase ||
  'Other';

const groupBySection = (lines) => {
  const groups = new Map();
  for (const l of lines || []) {
    const k = sectionKey(l);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(l);
  }
  return Array.from(groups.entries()).map(([name, items]) => ({ name, items }));
};

/* ─────────────────────── Main component ─────────────────────── */

const GRID = '74px minmax(220px, 1fr) 116px 108px 116px 132px 108px 120px 112px 108px 100px 34px';

const BillingG703 = ({
  details,
  isDraft,
  onUpdateLine,        // (lineId, field, value) => Promise
  onReleaseRetainage,  // (line) => void  — opens release modal
  pendingSaves,        // Set of "lineId:field" keys
  saveErrors,          // Map of "lineId:field" → error message
  progressAlerts,      // { total_alerts, critical_count, warning_count, days_until_end, alerts[] }
}) => {
  const lines = details?.lines || [];
  const grouped = groupBySection(lines);

  // Index schedule-progress alerts by SOV line so each row can flag its
  // worst severity (rose = critical, yellow = warning). Tasks without an
  // SOV allocation have null sov_line_id and only surface in the banner.
  const alertsByLine = new Map();
  for (const a of progressAlerts?.alerts || []) {
    if (a.sov_line_id == null) continue;
    if (!alertsByLine.has(a.sov_line_id)) alertsByLine.set(a.sov_line_id, []);
    alertsByLine.get(a.sov_line_id).push(a);
  }

  const totals = lines.reduce((acc, l) => {
    acc.sched += num(l.contract_amount);
    acc.prev += num(l.billed_to_date);
    acc.thisPeriod += num(l.this_period_earned_amount);
    acc.stored += num(l.stored_materials_this_period);
    acc.completed += num(l.total_completed_and_stored);
    acc.balance += num(l.remaining_balance);
    acc.retainage += num(l.retainage_amount);
    acc.released += num(l.retainage_released_this_period);
    return acc;
  }, { sched: 0, prev: 0, thisPeriod: 0, stored: 0, completed: 0, balance: 0, retainage: 0, released: 0 });
  const aggregatePct = totals.sched > 0 ? Math.min(100, (totals.completed / totals.sched) * 100) : 0;

  /* Header cell */
  const Th = ({ children, align = 'left', noRight = false }) => (
    <div style={{
      padding: '0 10px',
      display: 'flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : (align === 'center' ? 'center' : 'flex-start'),
      fontSize: 11.5, fontWeight: 600, color: 'var(--text-3)',
      textTransform: 'uppercase', letterSpacing: '0.04em',
      borderRight: noRight ? 'none' : '1px solid var(--border-subtle)',
      whiteSpace: 'nowrap',
    }}>{children}</div>
  );

  return (
    <div style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 12,
      boxShadow: 'var(--shadow-sm)',
      // overflow: hidden previously clipped the right-most G703 columns
      // (Retainage / Released / kebab) on standard laptop widths when
      // the sidebar + 12-col grid pushed past the viewport. overflow-x:
      // auto lets the user scroll those columns into view; overflow-y
      // stays hidden so the sticky head + sticky totals footer still
      // anchor cleanly inside the card.
      overflowX: 'auto', overflowY: 'hidden',
    }}>
      {/* Card header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '12px 16px',
        borderBottom: '1px solid var(--border-subtle)',
      }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>
          G703 — Continuation Sheet
        </h3>
        <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
          {lines.length} {lines.length === 1 ? 'line' : 'lines'}
        </span>
        <div style={{ flex: 1 }} />
        {!isDraft && (
          <span style={{ fontSize: 12.5, color: 'var(--text-3)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <LockIcon /> Locked — finalized
          </span>
        )}
      </div>

      {/* Schedule-progress alert banner (rose/yellow behind-schedule flags) */}
      <ProgressAlertBanner alerts={progressAlerts} />

      {/* Table region — fills the available viewport height instead of a
          fixed 520px box, so the G703 uses the room below it on tall
          screens. Floored at 520 so short laptops never regress; the
          ~460px subtrahend ≈ the chrome + KPI strip + AFP summary above
          the table, keeping the sticky head + sticky totals on-screen. */}
      <div style={{
        maxHeight: 'max(520px, calc(100vh - 460px))', overflow: 'auto',
        position: 'relative',
      }}>
        {/* Sticky table head */}
        <div style={{
          display: 'grid', gridTemplateColumns: GRID,
          height: 38,
          position: 'sticky', top: 0, zIndex: 3,
          background: 'var(--bg-surface-2)',
          borderBottom: '1px solid var(--border-subtle)',
        }}>
          <Th>Item</Th>
          <Th>Description</Th>
          <Th align="right">Sched. value</Th>
          <Th align="right">Previous</Th>
          <Th align="right">This period</Th>
          <Th>% Complete</Th>
          <Th align="right">Stored mat.</Th>
          <Th align="right">Total compl.</Th>
          <Th align="right">Balance</Th>
          <Th align="right">Retainage</Th>
          <Th align="right">Released</Th>
          <Th align="center" noRight />
        </div>

        {/* Body */}
        {grouped.map((g) => {
          // Per-section totals kept for the section-header dot color
          // (green at 100%). The visible summary trio that used to live
          // on the right side of the header was moved to G702 KPI tiles.
          const gSched = g.items.reduce((s, l) => s + num(l.contract_amount), 0);
          const gEarned = g.items.reduce((s, l) => s + num(l.total_completed_and_stored), 0);
          const pct = gSched > 0 ? (gEarned / gSched) * 100 : 0;
          const dotColor = pct >= 99.9 ? 'var(--success)' : 'var(--text-4)';
          return (
            <React.Fragment key={g.name}>
              {/* Sticky section subheader */}
              <div style={{
                display: 'grid', gridTemplateColumns: GRID,
                position: 'sticky', top: 38, zIndex: 2,
                background: 'var(--bg-surface-2)',
                borderBottom: '1px solid var(--border-subtle)',
              }}>
                <div style={{
                  gridColumn: '1 / 7',
                  padding: '8px 12px',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span style={{ color: 'var(--text-3)' }}><ChevronRight expanded /></span>
                  <span style={{ width: 7, height: 7, borderRadius: 99, background: dotColor }} />
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)' }}>{g.name}</span>
                  <span className="mono" style={{ fontSize: 12, color: 'var(--text-3)' }}>
                    {g.items.length}
                  </span>
                </div>
                {/* Right side of section header intentionally empty.
                    The Sched / Earned / Retainage summary trio that used
                    to live here was promoted to the G702 KPI tiles (now
                    Scheduled Value / Billed To Date / Retainage Held +
                    Billed This Period / % Billed / Billing Cycle #).
                    Keeping the grid column open lets section headers
                    grow into the space if we want to add per-section
                    actions later (rename, collapse, etc.). */}
              </div>

              {/* Rows */}
              {g.items.map((line) => {
                const sched = num(line.contract_amount);
                const prev = num(line.billed_to_date);
                const thisP = num(line.this_period_earned_amount);
                const stored = num(line.stored_materials_this_period);
                const completed = num(line.total_completed_and_stored);
                const balance = num(line.remaining_balance !== undefined ? line.remaining_balance : (sched - completed));
                const retainage = num(line.retainage_amount);
                const released = num(line.retainage_released_this_period);
                const fullyReleased = retainage > 0 && released >= retainage - 0.01;
                const pctComplete = num(line.current_percent_complete !== undefined ? line.current_percent_complete : (sched > 0 ? (completed / sched) * 100 : 0));
                const overBilled = (completed + stored) > sched + 0.01;

                const isPending = (field) => pendingSaves?.has(`${line.id}:${field}`);
                const errFor = (field) => saveErrors?.get(`${line.id}:${field}`);

                // Behind-schedule flag for this SOV line. Worst severity
                // wins: rose (critical, 20%+ behind) over yellow (warning,
                // 10%+ behind). The over-billed danger stripe takes
                // precedence on the left edge when both apply.
                const lineAlerts = alertsByLine.get(line.sov_line_id) || [];
                const alertSev = lineAlerts.some((a) => a.severity === 'critical')
                  ? 'critical'
                  : lineAlerts.some((a) => a.severity === 'warning')
                    ? 'warning'
                    : null;
                const alertStripe = alertSev === 'critical' ? 'var(--danger)' : alertSev === 'warning' ? 'var(--warning)' : null;
                const restBg = alertSev ? rowTint(alertSev) : 'var(--bg-surface)';
                const hoverBg = alertSev ? rowTintHover(alertSev) : 'var(--bg-hover)';
                const alertTip = lineAlerts.length
                  ? lineAlerts.map((a) => `${a.task_code || a.task_name}: ${a.message}`).join('\n')
                  : undefined;

                return (
                  <div key={line.id}
                  title={alertTip}
                  style={{
                    display: 'grid', gridTemplateColumns: GRID,
                    minHeight: 38,
                    borderBottom: '1px solid var(--border-subtle)',
                    background: restBg,
                    boxShadow: overBilled
                      ? 'inset 3px 0 0 var(--danger)'
                      : alertStripe ? `inset 3px 0 0 ${alertStripe}` : 'none',
                    transition: 'background .12s',
                    position: 'relative',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = hoverBg}
                  onMouseLeave={(e) => e.currentTarget.style.background = restBg}
                  >
                    {/* 1 — Item No. */}
                    <div className="mono" style={{
                      padding: '0 10px', display: 'flex', alignItems: 'center',
                      fontSize: 12, color: 'var(--text-4)',
                      letterSpacing: '-0.02em',
                    }}>
                      {line.sov_code || line.item_number || ''}
                    </div>
                    {/* 2 — Description */}
                    <div style={{
                      padding: '6px 10px', display: 'flex', flexDirection: 'column', justifyContent: 'center',
                      minWidth: 0,
                    }}>
                      <div style={{
                        fontSize: 14, fontWeight: 500, color: 'var(--text-1)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {line.sov_description || line.description || ''}
                      </div>
                      {line.building_area_phase && (
                        <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 1 }}>
                          {line.building_area_phase}
                        </div>
                      )}
                    </div>
                    {/* 3 — Sched. value */}
                    <div className="mono" style={{
                      padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                      fontSize: 13, fontWeight: 500,
                      color: sched === 0 ? 'var(--text-4)' : 'var(--text-1)',
                      letterSpacing: '-0.02em',
                    }}>{$f2(sched)}</div>
                    {/* 4 — Previous */}
                    <div className="mono" style={{
                      padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                      fontSize: 13, color: prev === 0 ? 'var(--text-4)' : 'var(--text-3)',
                      letterSpacing: '-0.02em',
                    }}>{$f2(prev)}</div>
                    {/* 5 — This period (computed; not editable) */}
                    <div className="mono" style={{
                      padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                      fontSize: 13, fontWeight: 500,
                      color: thisP === 0 ? 'var(--text-4)' : 'var(--text-1)',
                      letterSpacing: '-0.02em',
                    }}>{$f2(thisP)}</div>
                    {/* 6 — % Complete (editable on draft) */}
                    <div style={{ padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <div style={{
                        flex: 1, height: 4, borderRadius: 99,
                        background: 'var(--border-subtle)',
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${Math.min(100, Math.max(0, pctComplete))}%`, height: '100%',
                          background: pctComplete >= 99.9 ? 'var(--success)' : pctComplete > 0 ? 'var(--accent)' : 'transparent',
                          transition: 'width .15s',
                        }} />
                      </div>
                      {isDraft ? (
                        <EditCell
                          value={pctComplete}
                          onCommit={(v) => onUpdateLine(line.id, 'current_percent_complete', v)}
                          kind="percent"
                          align="right"
                          pending={isPending('current_percent_complete')}
                          errorMsg={errFor('current_percent_complete')}
                        />
                      ) : (
                        <span className="mono" style={{
                          fontSize: 12.5, color: pctComplete === 0 ? 'var(--text-4)' : 'var(--text-3)',
                          letterSpacing: '-0.02em',
                          minWidth: 40, textAlign: 'right',
                        }}>{pctComplete.toFixed(1)}%</span>
                      )}
                    </div>
                    {/* 7 — Stored materials (editable on draft) */}
                    {isDraft ? (
                      <EditCell
                        value={stored}
                        onCommit={(v) => onUpdateLine(line.id, 'stored_materials_this_period', v)}
                        pending={isPending('stored_materials_this_period')}
                        errorMsg={errFor('stored_materials_this_period')}
                      />
                    ) : (
                      <div className="mono" style={{
                        padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                        fontSize: 13, color: stored === 0 ? 'var(--text-4)' : 'var(--text-1)',
                        letterSpacing: '-0.02em',
                      }}>{$f2(stored)}</div>
                    )}
                    {/* 8 — Total completed */}
                    <div className="mono" style={{
                      padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                      fontSize: 13, fontWeight: 600,
                      color: completed === 0 ? 'var(--text-4)' : 'var(--text-1)',
                      letterSpacing: '-0.02em',
                    }}>{$f2(completed)}</div>
                    {/* 9 — Balance */}
                    <div className="mono" style={{
                      padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                      fontSize: 13,
                      color: balance < -0.01 ? 'var(--danger)' : balance < 0.01 ? 'var(--success)' : 'var(--text-1)',
                      letterSpacing: '-0.02em',
                    }}>{$f2(balance)}</div>
                    {/* 10 — Retainage */}
                    <div className="mono" style={{
                      padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                      fontSize: 13,
                      color: retainage > 0 ? 'var(--warning)' : 'var(--text-4)',
                      letterSpacing: '-0.02em',
                    }}>{$f2(retainage)}</div>
                    {/* 11 — Released */}
                    <div className="mono" style={{
                      padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                      gap: 6,
                      fontSize: 13,
                      color: released > 0 ? 'var(--success)' : 'var(--text-4)',
                      letterSpacing: '-0.02em',
                    }}>
                      {fullyReleased ? (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 8px', borderRadius: 999,
                          fontSize: 11.5, fontWeight: 500, color: 'var(--success)',
                          background: 'var(--success-soft)',
                          border: '1px solid color-mix(in oklch, var(--success) 22%, transparent)',
                        }}>Full</span>
                      ) : (
                        $f2(released)
                      )}
                    </div>
                    {/* 12 — kebab */}
                    <div style={{
                      padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {retainage > 0 && (
                        <button
                          type="button"
                          onClick={() => onReleaseRetainage(line)}
                          title="Release retainage"
                          style={{
                            width: 26, height: 26, borderRadius: 5,
                            background: 'transparent', border: 'none', color: 'var(--text-4)',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-surface-2)'; e.currentTarget.style.color = 'var(--text-2)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-4)'; }}
                        >
                          <KebabIcon />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}

        {/* Empty state */}
        {lines.length === 0 && (
          <div style={{
            padding: '36px 20px', textAlign: 'center',
            fontSize: 14.5, color: 'var(--text-4)',
          }}>
            No SOV lines on this pay application.
          </div>
        )}

        {/* Sticky totals footer */}
        {lines.length > 0 && (
          <div style={{
            display: 'grid', gridTemplateColumns: GRID,
            height: 40,
            position: 'sticky', bottom: 0, zIndex: 3,
            background: 'var(--bg-surface-2)',
            borderTop: '1px solid var(--border-default)',
          }}>
            <div style={{
              gridColumn: '1 / 3',
              padding: '0 12px',
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 12, fontWeight: 600, color: 'var(--text-2)',
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
              Totals
              <span className="mono" style={{
                fontSize: 12.5, fontWeight: 400, color: 'var(--text-4)', textTransform: 'none', letterSpacing: '-0.02em',
              }}>
                · {lines.length} lines
              </span>
            </div>
            <div className="mono" style={{ padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontSize: 13, fontWeight: 600, color: totals.sched === 0 ? 'var(--text-4)' : 'var(--text-1)', letterSpacing: '-0.02em' }}>{$f2(totals.sched)}</div>
            <div className="mono" style={{ padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontSize: 13, color: totals.prev === 0 ? 'var(--text-4)' : 'var(--text-3)', letterSpacing: '-0.02em' }}>{$f2(totals.prev)}</div>
            <div className="mono" style={{ padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontSize: 13, fontWeight: 600, color: totals.thisPeriod === 0 ? 'var(--text-4)' : 'var(--text-1)', letterSpacing: '-0.02em' }}>{$f2(totals.thisPeriod)}</div>
            <div style={{ padding: '0 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, height: 4, borderRadius: 99, background: 'var(--border-subtle)', overflow: 'hidden' }}>
                <div style={{ width: `${aggregatePct}%`, height: '100%', background: aggregatePct >= 99.9 ? 'var(--success)' : aggregatePct > 0 ? 'var(--accent)' : 'transparent' }} />
              </div>
              <span className="mono" style={{ fontSize: 12.5, color: aggregatePct === 0 ? 'var(--text-4)' : 'var(--text-3)', letterSpacing: '-0.02em', minWidth: 40, textAlign: 'right' }}>{aggregatePct.toFixed(1)}%</span>
            </div>
            <div className="mono" style={{ padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontSize: 13, color: totals.stored === 0 ? 'var(--text-4)' : 'var(--text-1)', letterSpacing: '-0.02em' }}>{$f2(totals.stored)}</div>
            <div className="mono" style={{ padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontSize: 13, fontWeight: 600, color: totals.completed === 0 ? 'var(--text-4)' : 'var(--text-1)', letterSpacing: '-0.02em' }}>{$f2(totals.completed)}</div>
            <div className="mono" style={{ padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontSize: 13, color: totals.balance < 0 ? 'var(--danger)' : 'var(--text-1)', letterSpacing: '-0.02em' }}>{$f2(totals.balance)}</div>
            <div className="mono" style={{ padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontSize: 13, color: totals.retainage > 0 ? 'var(--warning)' : 'var(--text-4)', letterSpacing: '-0.02em' }}>{$f2(totals.retainage)}</div>
            <div className="mono" style={{ padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontSize: 13, color: totals.released > 0 ? 'var(--success)' : 'var(--text-4)', letterSpacing: '-0.02em' }}>{$f2(totals.released)}</div>
            <div />
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingG703;
